/**
 * The wiring between the command bar and the command grammar.
 *
 * All of the thinking already happened: `commands/localCommands.ts` matches an utterance
 * and executes the intent, `commands/voice.ts` takes exactly one spoken phrase, and
 * `api/command.ts` is the fall-through for everything the grammar does not recognise.
 * This hook's only job is to build the `CommandEnv` those functions need out of a
 * `SessionView`, and to hold the three pieces of ephemeral UI state — the field, the
 * one-line reply, whether the mic button is held down.
 *
 * It is a hook rather than part of `CommandBar.tsx` so that the wiring can be tested
 * without a microphone, without a `SpeechRecognition` implementation and without the
 * network: every impure edge is an injectable option with a real default.
 *
 * ## What is deliberately *not* here
 *
 * No second grammar, no "did you mean", no command history. An utterance either matches
 * the local grammar (zero network, instant) or goes to `/api/command` once. If both fail
 * the player is told so in one line and nothing changes — the state they are looking at
 * is the state they played.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  interpretCommand,
  type ApiOptions,
  type CommandRequest,
  type LlmRequestOptions,
} from '../../api/index.js';
import { recordDebugEntry } from '../../state/debugLog.js';
import {
  handleUtterance,
  type CommandEnv,
  type CommandRequestContext,
  type LocalIntent,
  type SendCommand,
} from '../../commands/localCommands.js';
import {
  createSpeaker,
  isVoiceInputSupported,
  startPushToTalk,
  type PushToTalkSession,
  type SpeechRecognitionFactory,
} from '../../commands/voice.js';
import { useSessionView, type SessionView } from '../system/sessionView.js';

/**
 * One line of feedback.
 *
 * The `id` exists because the same sentence can be the honest answer twice in a row
 * ("Nothing to step back to yet.") and a toast keyed only on its text would not
 * re-appear the second time — which reads as the app having ignored the second attempt.
 */
export interface CommandMessage {
  id: number;
  text: string;
}

// ---------------------------------------------------------------------------
// The server fall-through
// ---------------------------------------------------------------------------

/**
 * Adapt `CommandRequestContext` — what the grammar knows — to `CommandRequest`, what the
 * route accepts. The endpoint disambiguates against the current chord only, so the rest
 * of `recentSymbols` stays on the device.
 *
 * `llm` is the player's model tier and their transparency choice, which the grammar knows
 * nothing about and should not: it is a fact about settings, not about the utterance.
 */
export function toCommandRequest(
  text: string,
  context: CommandRequestContext,
  llm: LlmRequestOptions = {},
): CommandRequest {
  const currentSymbol = context.recentSymbols.at(-1);
  return {
    utterance: text,
    perspectives: [...context.perspectives],
    adventurousness: context.adventurousness,
    ...(currentSymbol ? { currentSymbol } : {}),
    ...(context.intention === undefined ? {} : { intention: context.intention }),
    ...llm,
  };
}

/**
 * The `/api/command` port, as `handleUtterance` wants it.
 *
 * `getLlmOptions` is a getter rather than a value because a sender is built once and used
 * for the life of the bar, while the tier is a setting somebody can change between two
 * utterances — the same argument the effects layer makes for `getModelChoice`.
 */
export function createCommandSender(
  options?: ApiOptions,
  getLlmOptions?: () => LlmRequestOptions,
): SendCommand {
  return (text, context) =>
    interpretCommand(toCommandRequest(text, context, getLlmOptions?.()), {
      ...options,
      onDebug: (debug) => recordDebugEntry('command', debug),
    });
}

/**
 * What an unmatched utterance gets while the AI companion is off.
 *
 * It names the setting, because the player's next question is "how do I get that back?",
 * and it says the rest of the bar still works, because it does — step back, save, the key
 * question, adventurousness and "something else" are all grammar and all local.
 */
export const AI_COMPANION_OFF_REPLY =
  'That one needs the AI companion, which is off in Settings. The simple commands all still work.';

/**
 * The stand-in for `/api/command` while the companion is off.
 *
 * A `SendCommand` rather than a check somewhere earlier, because the question "did the
 * grammar recognise this?" is `handleUtterance`'s and there is no point asking it twice.
 * It answers with the one intent that is a sentence and nothing else — the same shape a
 * model returns for "what key are we in?" — so the line is toasted, spoken if spoken
 * replies are on, and changes nothing. **Nothing here touches the network.**
 */
export function createUnavailableCommandSender(): SendCommand {
  return () => Promise.resolve({ kind: 'answer', text: AI_COMPANION_OFF_REPLY });
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface UseCommandBarOptions {
  /** Overrides the view from context, exactly as `SessionPage` does. */
  view?: SessionView;
  /**
   * The server fall-through. `null` turns it off, which makes every unmatched utterance
   * an honest "did not catch that" with no request at all.
   */
  sendCommand?: SendCommand | null;
  /** Injected in tests. `null` means no dictation, so the mic button is not rendered. */
  createRecognition?: SpeechRecognitionFactory | null;
  /** Injected in tests, so nothing has to stub `speechSynthesis`. Still gated on settings. */
  speakImpl?: (text: string) => void;
  /** The explanation sheet lives in the page; the bar borrows it when it has one. */
  onShowExplanation?: (target: 'last-transition' | 'current-chord') => void;
  onShowVoicing?: () => void;
  /**
   * Overrides the view's `markLiked`. Return `false` for "there was no transition to
   * record" and the bar says so instead of leaving "Noted." standing over nothing.
   */
  onMarkLiked?: () => boolean | void;
}

export interface CommandBarController {
  text: string;
  setText: (text: string) => void;
  /** Run what is in the field (or the text passed in). Clears the field first. */
  submit: (raw?: string) => Promise<LocalIntent | null>;
  /** True while an utterance is in flight. Never blocks anything — it dims the pill. */
  busy: boolean;
  message: CommandMessage | null;
  dismissMessage: () => void;
  /** False where the platform has no speech recogniser. The mic button is then absent. */
  voiceSupported: boolean;
  talking: boolean;
  /** Press the mic. Resolves once the utterance has been heard *and* acted on. */
  startTalking: () => Promise<LocalIntent | null>;
  /** Release the mic. */
  stopTalking: () => void;
  cancelTalking: () => void;
}

export function useCommandBar(options: UseCommandBarOptions = {}): CommandBarController {
  const view = useSessionView(options.view);

  // Read the view and the callbacks through refs so the controller's identity does not
  // change on every chord — the bar sits under a graph that re-renders constantly, and a
  // new `submit` on each frame would be a new listener on each frame. Same idiom as
  // `state/sessionHost.tsx`.
  const viewRef = useRef(view);
  viewRef.current = view;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<CommandMessage | null>(null);
  const [talking, setTalking] = useState(false);

  const textRef = useRef(text);
  textRef.current = text;
  const messageId = useRef(0);
  const sessionRef = useRef<PushToTalkSession | null>(null);
  /**
   * The default sender, built once and reading the settings through the view ref at call
   * time. Rebuilding it per render would make `runUtterance` a new function every chord.
   */
  const senderRef = useRef<SendCommand | null>(null);
  senderRef.current ??= createCommandSender(undefined, () => {
    const settings = viewRef.current.settings;
    return {
      ...(settings?.modelChoice ? { modelChoice: settings.modelChoice } : {}),
      ...(settings?.showPrompts ? { debug: true } : {}),
    };
  });
  /** The local answer, built once beside the real sender and chosen at call time. */
  const unavailableSenderRef = useRef<SendCommand | null>(null);
  unavailableSenderRef.current ??= createUnavailableCommandSender();
  /** Set by `openSave`, awaited by `runUtterance`, so one submit is one whole thing. */
  const pendingRef = useRef<Promise<void> | null>(null);

  const say = useCallback((line: string) => {
    if (!line) return;
    messageId.current += 1;
    setMessage({ id: messageId.current, text: line });
  }, []);

  const buildEnv = useCallback((): CommandEnv => {
    const current = viewRef.current;
    const { onShowExplanation, onShowVoicing, onMarkLiked, speakImpl } = optionsRef.current;
    // Opt-in, and `undefined` rather than a no-op so `env.speak?.()` reads as "speaking is
    // not configured" — see the doctrine at the top of `commands/voice.ts`.
    const speak = current.settings?.speakReplies ? (speakImpl ?? createSpeaker(true)) : undefined;

    return {
      state: current.state,
      dispatch: current.dispatch,
      toast: say,
      openSave: (what) => {
        const saving = current.saveSketch?.(what) ?? Promise.resolve(null);
        // `applyIntent` toasts "Saving…" immediately after this returns, so anything said
        // here has to arrive later than that or it would be overwritten by the optimism.
        pendingRef.current = saving
          .then((receipt) => {
            if (!receipt) say('Nothing to keep yet.');
          })
          .catch(() => say('That would not save.'));
      },
      requestBranches: (hint) => {
        const nodeId = current.state.currentNodeId;
        if (!nodeId) return;
        // The options argument is omitted rather than passed as `undefined` when there is
        // no steer, so an unhinted regeneration is indistinguishable from the retry glyph.
        const steer = hint?.trim();
        if (steer) current.retryBranches?.(nodeId, { hint: steer });
        else current.retryBranches?.(nodeId);
      },
      showExplanation: (target) => {
        if (onShowExplanation) onShowExplanation(target);
        else say('Tap the chord to see why that works.');
      },
      showVoicing: () => {
        if (onShowVoicing) onShowVoicing();
        else say('Tap a branch card for its notes.');
      },
      markLiked: () => {
        // The host resolves *which* transition from the store, not from this render's
        // view — a chord landing between the phrase and the tap would otherwise be liked
        // instead of the one the player meant. Nothing wired at all counts as not
        // recorded: "Noted." over a preference nobody kept is the lie this replaced.
        const handler = onMarkLiked ?? current.markLiked;
        if (handler && handler() !== false) return;
        // `applyIntent` toasts "Noted." the moment this returns, so the correction has to
        // arrive after it — the same trick `openSave` uses for its receipt.
        pendingRef.current = Promise.resolve().then(() => say('Nothing to like yet.'));
      },
      ...(speak ? { speak } : {}),
    };
  }, [say]);

  const runUtterance = useCallback(
    async (raw: string): Promise<LocalIntent | null> => {
      const utterance = raw.trim();
      if (!utterance) return null;

      const { sendCommand } = optionsRef.current;
      // Read at call time, like everything else the settings decide: the companion can be
      // switched between two utterances. `=== false` and not `!== true`, so a view that
      // never described its settings keeps the fall-through it always had.
      const companionOff = viewRef.current.settings?.aiCompanion === false;
      const defaultSender = companionOff ? unavailableSenderRef.current : senderRef.current;
      const sender = sendCommand === undefined ? defaultSender : sendCommand;

      setBusy(true);
      try {
        const intent = await handleUtterance(utterance, buildEnv(), sender ?? undefined);
        const pending = pendingRef.current;
        if (pending) {
          pendingRef.current = null;
          await pending;
        }
        return intent;
      } catch (error) {
        // `handleUtterance` converts a FAILED REQUEST into the offline line itself; a
        // rejection reaching here means an intent threw while being APPLIED — an app
        // bug, not connectivity. Blaming the network would send the player to their
        // wifi settings for our defect, so say what actually happened and keep the bar
        // usable. The console gets the real error, which is where bug reports start.
        console.error('[hc] command failed while being applied:', error);
        say('That hit a problem in the app — nothing was changed.');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [buildEnv, say],
  );

  const submit = useCallback(
    (raw?: string): Promise<LocalIntent | null> => {
      const utterance = raw ?? textRef.current;
      // Cleared before the work, not after: the bar has to feel like it took the phrase.
      setText('');
      textRef.current = '';
      return runUtterance(utterance);
    },
    [runUtterance],
  );

  // -- push-to-talk ---------------------------------------------------------

  const { createRecognition } = options;
  const voiceSupported = useMemo(
    () => (createRecognition === undefined ? isVoiceInputSupported() : createRecognition !== null),
    [createRecognition],
  );

  const startTalking = useCallback(async (): Promise<LocalIntent | null> => {
    if (sessionRef.current) return null;
    const { createRecognition } = optionsRef.current;
    const session = startPushToTalk({
      // Null On the Go, where there is no microphone pipeline to hand over — which makes
      // this a no-op rather than a special case.
      recognizer: viewRef.current.recognizerControls ?? null,
      ...(createRecognition === undefined ? {} : { createRecognition }),
    });
    // Dictation being absent is an ordinary configuration, not something to report: the
    // button is not rendered, so there is nobody to report it to.
    if (!session) return null;

    sessionRef.current = session;
    setTalking(true);
    const transcript = await session.result;
    sessionRef.current = null;
    setTalking(false);
    return transcript ? runUtterance(transcript) : null;
  }, [runUtterance]);

  const stopTalking = useCallback(() => sessionRef.current?.stop(), []);
  const cancelTalking = useCallback(() => sessionRef.current?.cancel(), []);

  // Leaving the screen mid-utterance must still hand the microphone back to the piano.
  useEffect(
    () => () => {
      sessionRef.current?.cancel();
      sessionRef.current = null;
    },
    [],
  );

  const dismissMessage = useCallback(() => setMessage(null), []);

  return {
    text,
    setText,
    submit,
    busy,
    message,
    dismissMessage,
    voiceSupported,
    talking,
    startTalking,
    stopTalking,
    cancelTalking,
  };
}
